import { HttpService } from '@Services/index.ts';
import { useBoundStore } from '@Store/index.ts';
import { InstitutionIds } from '@Types/types.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { version } from '../../../package.json';
import type { bankTypes, IAvailablePromoCodes } from '@Store/slices/customer/customer.types.ts';
import type { ExperienceResult } from '@Services/convert/types';

interface ICreateLeadResponse {
  externalId: string;
  isNonUsSupported: boolean;
  isPartner: boolean;
  moreData: boolean;
  requireAuthentication: boolean;
  availablePromoCodes: IAvailablePromoCodes;
  bank: bankTypes;
  emailMfaActive: boolean;
  loginResponseCustomData?: {
    stateToken?: string;
  };
}

interface ICreateLeadPayload {
  firstName?: string;
  lastName?: string;
  email: string;
  password: string;
  experiences: ExperienceResult[];
}

const getEnrichedAdSource = ({
  adSource,
  obType,
  obVersion,
}: {
  adSource: string;
  obType: string;
  obVersion: string;
}): string => {
  try {
    const parsed = JSON.parse(decodeURIComponent(adSource));
    return JSON.stringify({ ...parsed, ob_type: obType, ob_version: obVersion });
  } catch {
    return adSource;
  }
};

export const createLead = async (payload: ICreateLeadPayload) => {
  const { adSource, referralCode, onboardingFlowIdentifier } =
    useBoundStore.getState().onboardingSlice;
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
  const { customerId } = useBoundStore.getState().customer;
  const setCustomerAccessToken = useBoundStore.getState().setCustomerAccessToken;
  const setOnboardingAuthenticated = useBoundStore.getState().setOnboardingAuthenticated;

  const obType =
    onboardingFlowIdentifier === OnboardingFlowIdentifier.PARTNER_EMBEDDED ? 'embedded' : 'regular';

  const enrichedAdSource = getEnrichedAdSource({ adSource, obType, obVersion: version });

  if (!isEmbedded) {
    setCustomerAccessToken('');
  }
  setOnboardingAuthenticated(false);

  const url = isEmbedded
    ? `v5/secureonboarding/partner/user/${customerId}`
    : 'v5/onboarding/customer/';

  const { data } = await HttpService.put<ICreateLeadResponse>(url, {
    firstName: payload.firstName || null,
    lastName: payload.lastName || null,
    email: payload.email,
    password: payload.password,
    bankCode: InstitutionIds.SUNRISE,
    fingerprint: window.fpId,
    referralCode,
    adSource: enrichedAdSource,
    abTests: payload.experiences?.map(({ id: flowId, variationKey }) => ({
      flowId,
      variationKey,
    })),
  });

  // v5 issues the session as an HttpOnly onboardingToken cookie for browsers (no body token), so
  // track that a session exists for the route guard. MFA-required responses are not a session yet.
  if (
    !data.emailMfaActive &&
    !data.loginResponseCustomData?.stateToken &&
    !data.requireAuthentication
  ) {
    setOnboardingAuthenticated(true);
  }

  return data;
};
